import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import boxOfferTicketIcon from "@images/illustrations/boxOfferTicketIcon.svg"
import { ArrowDrawIcon } from "@images/svgIcons/arrowDrawIcon"
import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import { useBoxConsumableActions } from "@modules/boxConsumable/hooks/useBoxConsumable"
import { creditPurchaseSchema } from "@modules/boxPurchases/forms/boxPurchasesCreditFormSchema"
import { word } from "@modules/core/utils/i18n"
import { LoaderCircle } from "lucide-react"
import { useImperativeHandle, useState } from "react"

export type BoxPurchasesCreditModalRef = {
  open: () => void
} & React.ComponentProps<"div">

export type BoxPurchasesCreditModalProps = {
  memberId: string
  consumableDetails: BoxConsumableDetailsEntity | null
  ref: React.Ref<BoxPurchasesCreditModalRef>
  className?: string
}

export function BoxPurchasesCreditModal({
  memberId,
  consumableDetails,
  ref,
  className,
  ...props
}: BoxPurchasesCreditModalProps) {
  const { creditPurchase, isCreditPurchasePending } = useBoxConsumableActions(memberId)
  const [showModal, setShowModal] = useState(false)

  const form = useAppForm({
    defaultValues: {
      newCredits: 0,
    },
    validators: {
      onSubmit: creditPurchaseSchema(consumableDetails?.count ?? null),
    },
    onSubmit: async ({ value: { newCredits } }) => {
      if (!consumableDetails) return

      await creditPurchase({ purchaseId: consumableDetails.id, newCredits })
        .then(() => {
          toast.success(
            newCredits > 0
              ? word("box.purchases.modal.credit.toast.success")
              : word("box.purchases.modal.credit.toast.decreaseSuccess"),
            { classNames: successToastClassName() },
          )
          onHide()
        })
        .catch(() => {
          toast.error(word("box.purchases.modal.credit.toast.error"), {
            classNames: errorToastClassName(),
          })
        })
    },
  })

  const onHide = () => {
    form.reset()
    setShowModal(false)
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  if (!consumableDetails) return null

  return (
    <Dialog
      open={showModal}
      onOpenChange={(open) => {
        if (!open) onHide()
      }}
    >
      <DialogContent className={cn("flex w-128 flex-col gap-8", className)} aria-describedby={undefined} {...props}>
        <DialogHeader>
          <DialogTitle>
            <div className="flex flex-col flex-wrap content-center justify-center gap-2 pt-8 text-center">
              <div className="flex w-full content-center justify-center">
                <img src={boxOfferTicketIcon} alt="boxOfferTicketIcon" />
              </div>
              <Text fontStyle="TL" className="text-textDefault-body">
                {word("box.purchases.modal.credit.header.title")}
              </Text>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* CONTENT */}
        <Text fontStyle="BM" className="flex content-center justify-center text-center text-textDefault-body">
          {word("box.purchases.modal.credit.header.subtitle")}
        </Text>

        <div className="flex flex-col">
          <form.AppField name="newCredits">
            {(field) => (
              <>
                <div className="flex w-full content-center justify-center gap-4">
                  <field.NumberCursor className="grow" />
                  <Text fontStyle="TS" className="flex items-center text-textDefault-body">
                    {word("box.purchases.modal.credit.content.inputTexte", { count: field.state.value })}
                  </Text>
                </div>
                <div className="flex pl-2">
                  <field.Message />
                </div>
              </>
            )}
          </form.AppField>
        </div>

        {consumableDetails.count !== null && (
          <form.Subscribe selector={(s) => s.values.newCredits}>
            {(newCredits) => {
              const colorText =
                newCredits < 0 ? "text-textError" : newCredits > 0 ? "text-textSuccess" : "text-textDefault-body"
              return (
                <div className="flex flex-col">
                  <div className="flex w-full content-center justify-center gap-6 text-center">
                    <div className="flex flex-col gap-1">
                      <Text fontStyle="LS" className="mb-2 text-textDefault-body">
                        {word("box.purchases.modal.credit.content.now")}
                      </Text>
                      <Text fontStyle="TS" className="text-textDefault-body">
                        {consumableDetails.count}
                      </Text>
                      <Text fontStyle="TS" className="text-textDefault-body">
                        {word("box.purchases.modal.credit.content.inputTexte", { count: consumableDetails.count ?? 0 })}
                      </Text>
                    </div>
                    <div className="flex items-end">
                      <ArrowDrawIcon color="currentColor" className="text-textDefault-body" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Text fontStyle="LS" className="mb-2 text-textDefault-body">
                        {word("box.purchases.modal.credit.content.then")}
                      </Text>
                      <Text fontStyle="TS" className={colorText}>
                        {(consumableDetails.count ?? 0) + newCredits}
                      </Text>
                      <Text fontStyle="TS" className={colorText}>
                        {word("box.purchases.modal.credit.content.inputTexte", {
                          count: (consumableDetails.count ?? 0) + newCredits,
                        })}
                      </Text>
                    </div>
                  </div>
                </div>
              )
            }}
          </form.Subscribe>
        )}

        {/* BUTTON */}
        <div className="mt-8 flex w-full flex-row items-center justify-center gap-4">
          <Button variant="outline" size="lg" onClick={onHide} className="grow">
            {word("global.cancel")}
          </Button>
          <form.Subscribe selector={(s) => [s.isSubmitting, s.values.newCredits] as const}>
            {([isSubmitting, newCredits]) => (
              <Button
                variant="primary"
                size="lg"
                onClick={() => void form.handleSubmit()}
                disabled={
                  isSubmitting ||
                  newCredits === 0 ||
                  isCreditPurchasePending ||
                  (consumableDetails.count !== null && newCredits < -consumableDetails.count)
                }
                className="grow"
              >
                {isCreditPurchasePending && <LoaderCircle className="animate-spin" />}
                {word("global.check")}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </DialogContent>
    </Dialog>
  )
}
